import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Paper, ButtonBase, Typography } from '@material-ui/core';
import BackspaceIcon from '@material-ui/icons/Backspace';

import Stack from 'components/shared/Stack';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { getCommunicationService } from 'services/communication/communication.service.factory';

const DialPadButton = styled(ButtonBase)`
  && {
    height: ${(props) => props.theme.spacing(4)};
    width: ${(props) => props.theme.spacing(4)};
    background-color: ${(props) => props.theme.palette.background.default};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    color: ${(props) => props.theme.palette.text.primary};
    padding: ${(props) => props.theme.spacing(4)};
    text-align: center;
    cursor: pointer;

    :hover {
      background: ${(props) => props.theme.palette.grey['500']};
      color: ${(props) => props.theme.palette.text.primary};
    }
  }
`;

const DialPadScreen = styled('div')`
  display: flex;
  align-items: center;
  margin: ${(props) => props.theme.spacing(0, -2)};
  background-color: ${(props) => props.theme.palette.background.default};
  position: relative;
`;

const DialPadScreenInput = styled('input')`
  width: 100%;
  padding: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.palette.text.primary};
  text-align: center;
  border: 0;
  font-size: ${(props) => props.theme.spacing(3)};
`;

const DialPadButtonContainer = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  grid-gap: 1rem;
  justify-items: center;
`;

const DialPadWrapper = styled(Paper)`
  && {
    padding: ${(props) => props.theme.spacing(2)};
  }
`;

const ClearScreenInputButton = styled(ButtonBase)`
  && {
    position: absolute;
    right: ${(props) => props.theme.spacing(2)};
  }
`;

const Dialpad = () => {
  const [digits, setDigits] = useState('');
  const leadCommunicationProvider = useSelector(
    (state: RootState) => state.lead.entity?.communicationProvider
  );

  useEffect(() => {
    if (leadCommunicationProvider && digits) {
      getCommunicationService(leadCommunicationProvider).sendDigits(digits.slice(-1));
    }
  }, [digits, leadCommunicationProvider]);

  const buttonChars = ['', 'ABC', 'DEF', 'GHI', 'JKL', 'MNO', 'PQRS', 'TUV', 'WXYZ', '*', '0', '#'];
  const handleKeypadButtonClick = (keypadChar: string) => () => {
    setDigits(digits + keypadChar);
  };
  const clearDialpadScreen = () => {
    if (digits) {
      setDigits('');
    }
  };

  return (
    <DialPadWrapper elevation={2}>
      <Stack>
        <DialPadScreen>
          <DialPadScreenInput type="text" value={digits} disabled />
          <ClearScreenInputButton onClick={clearDialpadScreen}>
            <BackspaceIcon color="action" />
          </ClearScreenInputButton>
        </DialPadScreen>
        <DialPadButtonContainer>
          {buttonChars.map((char, idx) => (
            <DialPadButton
              key={char}
              onClick={handleKeypadButtonClick(idx < 9 ? `${idx + 1}` : char)}
            >
              {idx < 9 ? <Typography variant="subtitle1">{idx + 1}</Typography> : null}
              <Typography variant="caption">{char}</Typography>
            </DialPadButton>
          ))}
        </DialPadButtonContainer>
      </Stack>
    </DialPadWrapper>
  );
};

export default Dialpad;
